/**
 * Which rows of a story list that is already on screen were re-ranked by a ranking that just arrived.
 * BetaStoryList pulses each the way it pulses an arrival (`.beta-story.is-arrived`, utils/storyArrivals), so
 * a story that changed places is marked where it landed.
 *
 * A move is judged among the stories both rankings hold, by name (`storyNames`: a story under a new face is
 * the same story), so stories arriving or leaving push rows along without moving them. The rows that moved
 * are the fewest whose removal leaves the rest in their old order (everything off the longest increasing
 * run of old positions): when one story climbs five places, it moved, not the five it passed.
 *
 * Deliberately not a move:
 * - anything in the list's first ranking (nothing was on screen to move);
 * - a ranking that reshuffles most of the list (more stories moved than kept their order): marking nearly
 *   every row marks nothing.
 */
import { storyNames } from './storyArrivals';

type StoryNames = Parameters<typeof storyNames>[0];

/** Indices (ascending) of one longest strictly increasing subsequence of *values*. */
function longestIncreasingRun(values: readonly number[]): number[] {
  const tails: number[] = [];
  const previous: number[] = [];
  values.forEach((value, index) => {
    let low = 0;
    let high = tails.length;
    while (low < high) {
      const middle = (low + high) >> 1;
      if (values[tails[middle]] < value) low = middle + 1;
      else high = middle;
    }
    previous[index] = low > 0 ? tails[low - 1] : -1;
    tails[low] = index;
  });
  const run: number[] = [];
  for (let index = tails.length ? tails[tails.length - 1] : -1; index >= 0; index = previous[index]) run.push(index);
  return run.reverse();
}

/** The ids of *stories* (in list order) that a ranking moved relative to the *previous* one. */
export function movedStoryIds(previous: readonly StoryNames[], stories: readonly StoryNames[]): string[] {
  const previousIndex = new Map<string, number>();
  previous.forEach((story, index) => storyNames(story).forEach(name => {
    if (!previousIndex.has(name)) previousIndex.set(name, index);
  }));
  // Each story of the previous ranking is claimed once, by the first story now known by one of its names.
  const claimed = new Set<number>();
  const kept: { id: string; was: number }[] = [];
  stories.forEach(story => {
    const indices = storyNames(story).map(name => previousIndex.get(name)).filter((index): index is number => index !== undefined);
    if (!indices.length) return;
    const was = Math.min(...indices);
    if (claimed.has(was)) return;
    claimed.add(was);
    kept.push({ id: story.id, was });
  });
  const inOrder = new Set(longestIncreasingRun(kept.map(story => story.was)));
  const moved = kept.length - inOrder.size;
  if (!moved || moved > inOrder.size) return [];
  const wasRank = new Map([...kept].sort((a, b) => a.was - b.was).map((story, rank) => [story.id, rank]));
  return kept.filter((story, rank) => !inOrder.has(rank) && wasRank.get(story.id) !== rank).map(story => story.id);
}
